"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, ShieldCheck, Scale, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
interface BreedItem {
  id: string;
  name: string;
  category: string;
  size: string;
  lifespan: string;
  energyLevel: string;
  temperamentOne: string;
  temperamentTwo: string;
  temperamentThree: string;
  summary: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  to: string;
}
const breeds: BreedItem[] = [{
  id: "golden-retriever",
  name: "Golden Retriever",
  category: "Sporting Group",
  size: "Large (55–75 lbs)",
  lifespan: "10–12 years",
  energyLevel: "High",
  temperamentOne: "Gentle",
  temperamentTwo: "Intelligent",
  temperamentThree: "Devoted",
  summary: "Renowned for their balanced demeanor and high trainability. Requires regular aerobic exercise and preventative cardiac screening.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/golden_retriever_breed.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/golden_retriever_breed.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/golden_retriever_breed.png-wvc-sizes",
  imageAlt: "Golden Retriever sitting outdoors with attentive expression",
  to: "/DogsPage"
}, {
  id: "labrador-retriever",
  name: "Labrador Retriever",
  category: "Sporting Group",
  size: "Large (55–80 lbs)",
  lifespan: "11–13 years",
  energyLevel: "High",
  temperamentOne: "Outgoing",
  temperamentTwo: "Active",
  temperamentThree: "Even-tempered",
  summary: "A versatile companion with robust physical stamina. Benefits from controlled joint care and structured daily nutritional management.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/labrador_retriever_breed.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/labrador_retriever_breed.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/labrador_retriever_breed.png-wvc-sizes",
  imageAlt: "Healthy Labrador Retriever looking forward in a park setting",
  to: "/DogsPage"
}, {
  id: "french-bulldog",
  name: "French Bulldog",
  category: "Non-Sporting Group",
  size: "Small (16–28 lbs)",
  lifespan: "10–12 years",
  energyLevel: "Moderate",
  temperamentOne: "Playful",
  temperamentTwo: "Adaptable",
  temperamentThree: "Affectionate",
  summary: "Compact urban companion with a gentle disposition. Requires attentive respiratory monitoring and climate-controlled exercise routines.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/french_bulldog_breed.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/french_bulldog_breed.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/french_bulldog_breed.png-wvc-sizes",
  imageAlt: "Alert French Bulldog sitting in a bright living space",
  to: "/DogsPage"
}, {
  id: "german-shepherd",
  name: "German Shepherd",
  category: "Herding Group",
  size: "Large (50–90 lbs)",
  lifespan: "9–13 years",
  energyLevel: "High",
  temperamentOne: "Courageous",
  temperamentTwo: "Confident",
  temperamentThree: "Trainable",
  summary: "Highly capable working breed with notable loyalty and problem-solving drive. Requires consistent mental stimulation and early orthopedic support.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/german_shepherd_breed.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/german_shepherd_breed.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/german_shepherd_breed.png-wvc-sizes",
  imageAlt: "German Shepherd displaying calm, focused posture outdoors",
  to: "/DogsPage"
}];
export default function DogBreeds() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="dog-breeds-directory" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-px w-6 bg-accent" />
            <span className="text-xs font-bold tracking-widest text-accent uppercase">
              Canine Directory
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Explore canine breed profiles
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Review clinical traits, temperament baselines, and lifespan data
            verified by veterinary literature to understand each breed&apos;s
            care requirements.
          </p>

          {/* Clinical Governance Callout */}
          <div className="mt-6 flex items-start gap-3.5 rounded-r-lg border border-border border-l-4 border-l-accent bg-card p-4 text-xs sm:text-sm text-muted-foreground">
            <ShieldCheck className="size-5 text-accent shrink-0 mt-0.5" />
            <p className="leading-normal">
              <strong className="font-semibold text-foreground">
                Veterinary review standard:
              </strong>{" "}
              Lifespan estimates and health markers reflect consolidated data
              from veterinary teaching hospitals and breed registries.
            </p>
          </div>
        </div>

        {/* Directory Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {breeds.map((breed, index) => <motion.div key={breed.id} variants={cardVariants} data-index={index}>
              <Card className="group h-full bg-card border border-border rounded-2xl overflow-hidden py-0 gap-0 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-border/80">
                {/* Image Soft Aperture Container */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img src={breed.imageSrc} data-wvc-srcset={breed.imageSrcset} data-wvc-sizes={breed.imageSizes} alt={breed.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Category Pill Over Image */}
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="bg-background/95 backdrop-blur-xs text-foreground font-medium text-xs px-2.5 py-1 border border-border shadow-2xs">
                      {breed.category}
                    </Badge>
                  </div>
                </div>

                {/* Card Content Area */}
                <CardContent className="p-6 sm:p-7 flex flex-col flex-grow justify-between gap-6">
                  <div>
                    {/* Header Details */}
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                      <h3 className="font-serif text-2xl font-semibold tracking-tight text-foreground">
                        {breed.name}
                      </h3>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {breed.summary}
                    </p>

                    {/* Vitals Matrix */}
                    <div className="grid grid-cols-2 gap-3 p-3.5 rounded-lg bg-background border border-border mb-6">
                      <div className="flex items-center gap-2">
                        <Scale className="size-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                            Size
                          </p>
                          <p className="text-xs sm:text-sm font-semibold text-foreground">
                            {breed.size}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock className="size-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                            Lifespan
                          </p>
                          <p className="text-xs sm:text-sm font-semibold text-foreground">
                            {breed.lifespan}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Temperament Tags */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                        <Sparkles className="size-3.5 text-accent" />
                        Temperament Traits
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground border border-border">
                          {breed.temperamentOne}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground border border-border">
                          {breed.temperamentTwo}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground border border-border">
                          {breed.temperamentThree}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Profile Action Link */}
                  <div className="pt-4 border-t border-border flex items-center justify-between">
                    <Button asChild variant="outline" className="w-full justify-between h-11 px-4 text-sm font-semibold bg-background hover:bg-secondary text-foreground border-border hover:border-border transition-colors group/btn">
                      <Link to={breed.to}>
                        <span>Read complete profile</span>
                        <ArrowRight className="size-4 text-accent transition-transform duration-200 group-hover/btn:translate-x-0.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Footer Callout */}
        <div className="mt-12 sm:mt-16 text-center border-t border-border pt-10">
          <p className="text-sm text-muted-foreground mb-4">
            Need guidance comparing multiple breeds for your home environment?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-semibold px-6 h-11">
            <Link to="/DogsPage">View all canine health guides</Link>
          </Button>
        </div>
      </div>
    </section>;
}